import { toString } from "currency";
import { TouchableWithoutFeedback, View } from "react-native";
import { Badge, Text } from "react-native-paper";
import { useTailwind } from "tailwind-rn";

import ImageWithFallback from "../../../components/ImageWithFallback";
import Label from "../../../components/Label";
import { CartReviewProduct } from "../types";

type Props = {
  product: CartReviewProduct;
  handleCartItemSelect: () => void;
};

export default function CartReviewItem({
  product,
  handleCartItemSelect,
}: Props) {
  const { price, quantity, currency, name, variant_options } = product;

  const amount = price * quantity;
  const priceWithCurrency = toString({ amount, currency });
  const unitPriceWithCurrency = toString({ amount: price, currency });
  const variantOptions = Object.values(variant_options || {});

  const tailwind = useTailwind();

  return (
    <TouchableWithoutFeedback onPress={handleCartItemSelect}>
      <View style={tailwind("flex-row justify-between")}>
        <View style={tailwind("relative")}>
          <ImageWithFallback
            fallbackIcon={product.uuid ? "package-variant-closed" : "cash"}
            source={
              product.images?.length ? product.images[0] : product.image_url
            }
          />
          <Badge style={tailwind("absolute top-0 right-0")}>{quantity}</Badge>
        </View>

        <View style={tailwind("flex-1 justify-center px-4")}>
          <Text variant="titleMedium" numberOfLines={2}>
            {name}
          </Text>

          {variantOptions && <VariantOptions options={variantOptions} />}
        </View>

        {quantity > 1 ? (
          <View style={tailwind("items-end")}>
            <Text variant="titleMedium">{priceWithCurrency}</Text>
            <Text variant="titleSmall">{`${quantity}x ${unitPriceWithCurrency}`}</Text>
          </View>
        ) : (
          <View style={tailwind("self-center")}>
            <Text variant="titleMedium">{priceWithCurrency}</Text>
          </View>
        )}
      </View>
    </TouchableWithoutFeedback>
  );
}

function VariantOptions({ options }: { options: string[] }) {
  const tailwind = useTailwind();
  return (
    // The parent View has a negative left margin equal to the left margin of the child View.
    // This ensures that the first item of each row aligns properly with the parent container,
    // and the left margin is only applied between the items.
    <View style={tailwind("flex-row flex-wrap pt-1 -ml-1")}>
      {options.map((option, index) => (
        <Label key={index} name={option} style={tailwind("ml-1 mb-1")} />
      ))}
    </View>
  );
}
